import type { RouteObject } from 'react-router-dom'

import { RequireAuth } from './auth'
import { Approvals } from './pages/Approvals'
import { Catalog } from './pages/Catalog'
import { CatalogItemPage } from './pages/CatalogItem'
import { Changes } from './pages/Changes'
import { Home } from './pages/Home'
import { KbArticlePage, KbList } from './pages/Kb'
import { Login } from './pages/Login'
import { NewTicket } from './pages/NewTicket'
import { Notifications } from './pages/Notifications'
import { PortalPending } from './pages/Pending'
import { Profile } from './pages/Profile'
import { PublicApprovalPage } from './pages/PublicApproval'
import { TicketDetailPage } from './pages/TicketDetail'
import { TicketList } from './pages/TicketList'

import type { ReactNode } from 'react'

const guarded = (element: ReactNode) => <RequireAuth>{element}</RequireAuth>

// SPEC section 8: /login, /, /new, /tickets, /tickets/:ref, /kb, /kb/:slug, /changes
// Phase E module 4 adds /catalog and /catalog/:id — the catalog is the entry
// point, and /new remains the direct route for "just raise something".
//
// The portal redesign adds /requests, /profile, and destinations that render an
// honest capability state because no portal API can serve them: /assets and
// /notifications. They are routed rather than hidden so the navigation is
// complete and every row opens a real page — the gap for each is recorded in
// docs/PORTAL-BACKEND-GAPS.md.
//
// /approvals was one of those three until the full-site audit found the API had
// since been built. It is a real screen now.
export const portalRoutes: RouteObject[] = [
  { index: true, element: guarded(<Home />) },
  { path: 'login', element: <Login /> },
  { path: 'catalog', element: guarded(<Catalog />) },
  { path: 'catalog/:id', element: guarded(<CatalogItemPage />) },
  { path: 'new', element: guarded(<NewTicket />) },
  { path: 'tickets', element: guarded(<TicketList />) },
  { path: 'tickets/:ref', element: guarded(<TicketDetailPage />) },
  // Same component, filtered to `type == request`. A request and an incident
  // differ by one field, so two screens would be two copies of one list.
  { path: 'requests', element: guarded(<TicketList requestsOnly />) },
  { path: 'kb', element: guarded(<KbList />) },
  { path: 'kb/:slug', element: guarded(<KbArticlePage />) },
  { path: 'changes', element: guarded(<Changes />) },
  { path: 'profile', element: guarded(<Profile />) },
  // Approvals is a real screen now — the manager sign-off API landed and this
  // route was still rendering a capability state that said it had not. See
  // pages/Approvals.tsx.
  { path: 'approvals', element: guarded(<Approvals />) },
  { path: 'approvals/:token', element: <PublicApprovalPage /> },
  { path: 'approvals/:token/:action', element: <PublicApprovalPage /> },
  { path: 'assets', element: guarded(<PortalPending topic="assets" />) },
  { path: 'notifications', element: guarded(<Notifications />) },
]
